import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckSquare, Square, ArrowDownToLine, Loader2, CheckCircle, FileText } from 'lucide-react'
import api from '../../services/api'

interface MendeleyDocument {
  id: string
  title: string
  authors?: string[]
  year?: number
  folder?: string
  type?: string
}

interface MendeleyLibrary {
  documents: MendeleyDocument[]
  folders: string[]
  total: number
}

export default function MendeleyImport() {
  const queryClient = useQueryClient()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [importResult, setImportResult] = useState<{ count: number } | null>(null)

  const { data: library, isLoading } = useQuery<MendeleyLibrary>({
    queryKey: ['mendeleyLibrary'],
    queryFn: async () => {
      const { data } = await api.get('/academic/mendeley/library')
      return data
    },
  })

  const importMutation = useMutation({
    mutationFn: async (documentIds: string[]) => {
      const { data } = await api.post('/academic/mendeley/import', {
        document_ids: documentIds,
      })
      return data
    },
    onSuccess: (data) => {
      setImportResult({ count: data.imported_count || selectedIds.size })
      setSelectedIds(new Set())
      queryClient.invalidateQueries({ queryKey: ['mendeleyLibrary'] })
    },
  })

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const selectAll = () => {
    if (library?.documents) {
      setSelectedIds(new Set(library.documents.map((d) => d.id)))
    }
  }

  const deselectAll = () => {
    setSelectedIds(new Set())
  }

  const handleImport = () => {
    if (selectedIds.size > 0) {
      importMutation.mutate(Array.from(selectedIds))
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 text-acad-500 animate-spin" />
        <span className="ml-2 text-sm text-gray-500">Loading Mendeley library...</span>
      </div>
    )
  }

  if (importResult) {
    return (
      <div className="text-center py-6">
        <CheckCircle className="mx-auto h-10 w-10 text-green-500 mb-3" />
        <p className="text-base font-medium text-gray-900">
          Successfully imported {importResult.count} item{importResult.count !== 1 ? 's' : ''}
        </p>
        <button
          onClick={() => setImportResult(null)}
          className="mt-3 text-sm font-medium text-acad-600 hover:text-acad-700"
        >
          Import more
        </button>
      </div>
    )
  }

  const documents = library?.documents || []

  if (documents.length === 0) {
    return (
      <div className="text-center py-6">
        <FileText className="mx-auto h-8 w-8 text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">No documents found in your Mendeley library.</p>
      </div>
    )
  }

  return (
    <div>
      {/* Selection controls */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <button
            onClick={selectAll}
            className="text-xs font-medium text-acad-600 hover:text-acad-700"
          >
            Select All
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={deselectAll}
            className="text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            Deselect All
          </button>
        </div>
        <span className="text-xs text-gray-400">
          {selectedIds.size} of {documents.length} selected
        </span>
      </div>

      {/* Document list */}
      <div className="space-y-1 max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
        {documents.map((doc) => {
          const isSelected = selectedIds.has(doc.id)
          return (
            <div
              key={doc.id}
              onClick={() => toggleSelect(doc.id)}
              className={`flex items-start gap-3 p-3 cursor-pointer transition-colors ${
                isSelected ? 'bg-acad-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex-shrink-0 mt-0.5">
                {isSelected ? (
                  <CheckSquare className="h-4 w-4 text-acad-600" />
                ) : (
                  <Square className="h-4 w-4 text-gray-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{doc.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {doc.authors && doc.authors.length > 0 && (
                    <span className="text-xs text-gray-500 truncate">
                      {doc.authors.slice(0, 2).join(', ')}
                      {doc.authors.length > 2 ? ` +${doc.authors.length - 2}` : ''}
                    </span>
                  )}
                  {doc.year && <span className="text-xs text-gray-400">{doc.year}</span>}
                  {doc.folder && (
                    <span className="text-xs text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                      {doc.folder}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Import error */}
      {importMutation.isError && (
        <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-700">
          Import failed. Please try again.
        </div>
      )}

      {/* Import button */}
      <div className="mt-4">
        <button
          onClick={handleImport}
          disabled={selectedIds.size === 0 || importMutation.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-acad-500 px-4 py-2 text-sm font-medium text-white hover:bg-acad-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {importMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Importing...
            </>
          ) : (
            <>
              <ArrowDownToLine className="h-4 w-4" />
              Import Selected ({selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''})
            </>
          )}
        </button>
      </div>
    </div>
  )
}
